import Groq from 'groq-sdk';
import { CVData } from '../types';

const groq = new Groq({
  apiKey: import.meta.env.VITE_GROQ_API_KEY,
  dangerouslyAllowBrowser: true
});

// Modelos recomendados de Groq
const MODELS = {
  // Modelo más avanzado para análisis de trabajos
  JOB_ANALYSIS: 'llama-3.3-70b-versatile', // Production
  // Modelo para generación de CV
  CV_GENERATION: 'meta-llama/llama-4-scout-17b-16e-instruct', // Preview - Latest
  // Modelo para análisis de mejoras
  IMPROVEMENT_ANALYSIS: 'openai/gpt-oss-120b', // Production
  // Modelo para carta de presentación
  COVER_LETTER: 'llama-3.3-70b-versatile', // Production
  // Modelo para chatbot
  CHATBOT: 'llama-3.3-70b-versatile' // Production
};

export const getJobDetails = async (jobDescription: string) => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.JOB_ANALYSIS,
      messages: [
        {
          role: "system",
          content: "Eres un experto en análisis de ofertas de trabajo. Tu tarea es extraer y estructurar la información clave de la descripción del puesto proporcionada."
        },
        {
          role: "user",
          content: `Por favor, analiza la siguiente descripción del trabajo y proporciona un resumen estructurado que incluya:\n\n1. Título del puesto\n2. Empresa (si se menciona)\n3. Requisitos clave\n4. Responsabilidades principales\n5. Tecnologías/herramientas requeridas\n6. Beneficios ofrecidos (si se mencionan)\n\nDescripción del trabajo:\n${jobDescription}\n\nFormatea la respuesta en Markdown.`
        }
      ],
      temperature: 0.2,
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error('Error analizando trabajo:', error);
    throw error;
  }
};

// Función principal, ahora solo genera el CV y devuelve resultados parciales
export const generateOptimizedCV = async (jobUrl: string, userCV: string, jobDetails: string) => {
  try {
    // Primera fase: Generar solo el CV optimizado
    const cvData = await generateCVOnly(jobUrl, userCV, jobDetails);

    // Devolvemos inmediatamente los datos del CV para mostrar en la UI
    return {
      cv: cvData,
      coverLetter: null, // Se generará después
      analysis: null     // Se generará después
    };
  } catch (error) {
    console.error('Error generando CV:', error);
    throw error;
  }
};

// Nueva función que solo genera el CV
export const generateCVOnly = async (jobUrl: string, userCV: string, jobDetails: string) => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.CV_GENERATION,
      messages: [
        {
          role: "system",
          content: `Eres un experto en optimización de CVs. Tu tarea es analizar el CV del usuario y la descripción del trabajo para generar un CV optimizado que se ajuste perfectamente al diseño proporcionado.

          INSTRUCCIONES DETALLADAS:

          1. Analiza el CV del usuario y extrae toda la información relevante
          2. Compara con los requisitos del trabajo
          3. Para cada experiencia laboral, incluye:
             - Descripción general del rol
             - Al menos 4 logros o responsabilidades específicas
             - Tecnologías y herramientas utilizadas
          
          ⚠️ CRÍTICO - INCLUIR TODAS LAS EXPERIENCIAS:
          - Debes incluir TODAS las experiencias laborales que encuentres en el CV del usuario
          - NO omitas ninguna experiencia, incluso si parece menos relevante
          - Si el CV tiene 3 empleos, el JSON debe tener 3 en el array "experience"
          - Si el CV tiene 5 empleos, el JSON debe tener 5 en el array "experience"
          - NUNCA reduzcas el número de experiencias del CV original
          - Mantén el orden cronológico (más reciente primero)
          
          IMPORTANTE: Considera como experiencia laboral todos los siguientes tipos de actividades:
          - Empleos formales
          - Internados profesionales
          - Residencias
          - Prácticas profesionales
          - Pasantías
          - Voluntariados relacionados con la profesión
          - Cualquier actividad donde el candidato haya desempeñado funciones profesionales, incluso en formación

          IMPORTANTE SOBRE EDUCACIÓN:
          Debes incluir en la sección "education" TODOS los siguientes tipos de formación:
          - Títulos universitarios (grados, licenciaturas)
          - Postgrados
          - Másters
          - Maestrías o Magísteres
          - Doctorados
          - Diplomados
          - Especialidades formales
          - Cualquier formación académica oficial que otorgue un título o certificación formal

          La sección "courses" debe incluir SOLO:
          - Cursos cortos o workshops
          - Certificaciones técnicas no académicas
          - Seminarios
          - Talleres
          - Actividades formativas breves o complementarias

          4. Optimiza y estructura la información para que se ajuste al siguiente formato JSON:

          {
            "name": "Nombre completo",
            "title": "Título profesional actual o deseado",
            "contact": {
              "phone": "Número de teléfono con formato internacional",
              "email": "Correo electrónico profesional",
              "location": "Ciudad, País"
            },
            "about": "Resumen profesional conciso (máximo 300 caracteres)",
            "experience": [
              {
                "title": "Cargo específico",
                "company": "Nombre de la empresa",
                "period": "Periodo (ej: 2020 - Presente)",
                "description": "Descripción general del rol",
                "achievements": [
                  "Logro específico 1",
                  "Logro específico 2",
                  "Logro específico 3",
                  "Logro específico 4"
                ],
                "technologies": [
                  "Tecnología 1",
                  "Tecnología 2",
                  "Tecnología 3"
                ]
              }
            ],
            "education": [
              {
                "degree": "Título obtenido",
                "institution": "Nombre de la institución",
                "period": "Periodo de estudio"
              }
            ],
            "courses": [
              {
                "title": "Nombre del curso o actividad",
                "institution": "Nombre de la institución",
                "period": "Periodo (ej: Marzo 2022)",
                "description": "Breve descripción (opcional)"
              }
            ],
            "languages": [
              {
                "name": "Nombre del idioma",
                "level": "Nivel (ej: Nativo, Avanzado, etc.)"
              }
            ],
            "skills": [
              "Habilidad1",
              "Habilidad2",
              "Habilidad3"
            ]
          }
          
          INSTRUCCIONES IMPORTANTES SOBRE HABILIDADES:
          1. Las habilidades deben ser palabras únicas o frases muy cortas (2-3 palabras máximo)
          2. Ejemplos correctos: "Python", "SQL", "Power BI", "Microsoft Excel", "Marketing Digital"
          3. Ejemplos incorrectos: "Experiencia en el análisis avanzado de datos", "Capacidad para liderar equipos multidisciplinarios"
          4. Incluir MÁXIMO 6-8 habilidades, priorizando las más relevantes para el puesto
          5. Enfocarse en habilidades técnicas y herramientas específicas
          
          ATENCIÓN - REGLAS CRÍTICAS PARA HABILIDADES:
          1. NO usar NUNCA prefijos como "Programación en", "Uso de", "Implementación de", "Experiencia en", etc.
          2. Usar SOLO el nombre de la tecnología, herramienta o habilidad específica
          3. Para lenguajes de programación: escribir solo "Python", "JavaScript", "Java", etc.
          4. Para herramientas: escribir solo "Excel", "Power BI", "Tableau", etc.
          5. Para metodologías: escribir solo "Scrum", "Kanban", "Design Thinking", etc.
          6. Las habilidades deben tener un MÁXIMO de 2 palabras, excepcionalmente 3
          7. Cada habilidad debe ocupar poco espacio visual en el CV
          
          EJEMPLOS CORRECTOS DE HABILIDADES:
          ["Python", "SQL", "Excel", "Power BI", "Tableau", "R", "Git", "Docker", "AWS", "Azure"]
          
          EJEMPLOS INCORRECTOS DE HABILIDADES:
          ["Programación en Python", "Implementación de bases de datos SQL", "Uso avanzado de Excel", "Experiencia con Power BI"]
          
          IMPORTANTE: Debes responder ÚNICAMENTE con un objeto JSON válido, sin texto adicional antes o después.`
        },
        {
          role: "user",
          content: `URL del trabajo: ${jobUrl}\n\nDetalles del trabajo:\n${jobDetails}\n\nCV actual del usuario:\n${userCV}\n\nPor favor, genera un CV optimizado en formato JSON siguiendo la estructura especificada.`
        }
      ],
      temperature: 0.2,
      response_format: { type: "json_object" }
    });

    const cvData = {
      ...JSON.parse(completion.choices[0].message.content || '{}'),
      photo: undefined,
      template: 'template1' // Template por defecto
    } as CVData;

    // Aquí podemos añadir lógica para reclasificar diplomados, maestrías, etc. si han quedado en courses
    if (cvData.courses && cvData.courses.length > 0) {
      const coursesToMove = [];
      const remainingCourses = [];

      // Palabras clave que indican que un "curso" debería ser educación formal
      const educationKeywords = [
        'diplomado', 'maestría', 'magister', 'magíster', 'master', 'máster',
        'especialización', 'especialidad', 'postgrado', 'postítulo',
        'doctorado', 'phd', 'mba'
      ];

      // Revisamos cada curso para ver si debe reclasificarse
      for (const course of cvData.courses) {
        const title = course.title.toLowerCase();
        if (educationKeywords.some(keyword => title.includes(keyword))) {
          // Es educación formal, movemos a education
          coursesToMove.push({
            degree: course.title,
            institution: course.institution,
            period: course.period
          });
        } else {
          // Es un curso corto, lo dejamos en courses
          remainingCourses.push(course);
        }
      }

      // Actualizamos las secciones
      if (coursesToMove.length > 0) {
        cvData.education = [...(cvData.education || []), ...coursesToMove];
        cvData.courses = remainingCourses;

        // Ordenamos education por fecha (más reciente primero)
        cvData.education.sort((a, b) => {
          // Extraemos años para comparar
          const yearA = extractLatestYear(a.period);
          const yearB = extractLatestYear(b.period);
          return yearB - yearA; // Orden descendente
        });
      }
    }

    return cvData;
  } catch (error) {
    console.error('Error generando datos del CV:', error);
    throw error;
  }
};

// Genera solo el análisis de mejoras después
export const generateImprovementAnalysis = async (cvData: CVData, jobDetails: string) => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.IMPROVEMENT_ANALYSIS,
      messages: [
        {
          role: "system",
          content: `Eres un experto en análisis de carreras y desarrollo profesional. Tu tarea es analizar el CV del candidato y los requisitos del trabajo para proporcionar un análisis detallado de áreas de mejora y recomendaciones.

          Debes proporcionar un análisis en el siguiente formato JSON:

          {
            "overallScore": "Puntuación general del 1 al 10",
            "strengths": [
              "Punto fuerte 1",
              "Punto fuerte 2",
              "Punto fuerte 3"
            ],
            "weaknesses": [
              "Debilidad 1",
              "Debilidad 2",
              "Debilidad 3"
            ],
            "improvements": [
              {
                "category": "Categoría de la mejora (ej: Habilidades Técnicas, Certificaciones, etc.)",
                "title": "Título descriptivo",
                "description": "Descripción breve del área de mejora",
                "priority": "high | medium | low",
                "score": "Puntuación actual (0-100)",
                "recommendations": [
                  "Recomendación específica 1",
                  "Recomendación específica 2",
                  "Recomendación específica 3"
                ]
              }
            ]
          }

          IMPORTANTE:
          - La puntuación general debe ser realista y considerar tanto fortalezas como debilidades
          - Incluir 3-4 puntos fuertes y débiles principales
          - Proporciona 4-5 áreas de mejora
          - Cada área debe tener 3-4 recomendaciones específicas y accionables
          - Las puntuaciones deben ser realistas y basadas en la comparación entre el CV y los requisitos
          - Las prioridades deben reflejar la importancia para el puesto específico
          
          IMPORTANTE: Debes responder ÚNICAMENTE con un objeto JSON válido.`
        },
        {
          role: "user",
          content: `CV del candidato:\n${JSON.stringify(cvData, null, 2)}\n\nDetalles del trabajo:\n${jobDetails}\n\nPor favor, genera un análisis detallado de áreas de mejora y recomendaciones en el formato JSON especificado.`
        }
      ],
      temperature: 0.2,
      response_format: { type: "json_object" }
    });

    return JSON.parse(completion.choices[0].message.content || '{}');
  } catch (error) {
    console.error('Error generando análisis:', error);
    throw error;
  }
};

// Genera solo la carta de presentación después
export const generateCoverLetter = async (cvData: CVData, jobDetails: string) => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.COVER_LETTER,
      messages: [
        {
          role: "system",
          content: `Eres un experto en redacción de cartas de presentación profesionales. Tu tarea es crear una carta personalizada basándote EXCLUSIVAMENTE en la información REAL del CV del candidato y la descripción del trabajo.

          REGLAS CRÍTICAS - LEE ESTO CON ATENCIÓN:
          
          1. NUNCA uses placeholders como "[Nombre de la Empresa]", "[Título del Puesto]", "[nombre del reclutador]", etc.
          2. NUNCA uses corchetes [] en el texto final
          3. Si NO tienes información específica (como nombre de empresa o reclutador), OMITE esa parte o usa un saludo genérico
          4. USA SOLO información que esté presente en el CV o en la descripción del trabajo
          5. Si la descripción del trabajo menciona la empresa, úsala. Si no, NO inventes ni dejes placeholder
          6. Si no sabes el nombre del reclutador, usa "Estimado/a Equipo de Recursos Humanos" o similar
          
          ESTRUCTURA DE LA CARTA:
          
          Párrafo 1 - Introducción:
          - Saludo (si no hay nombre específico, usa "Estimado/a Equipo de Recursos Humanos" o "Estimado/a Responsable de Selección")
          - Menciona el puesto al que postula (usa el título del CV o el puesto mencionado en la descripción)
          - Breve presentación (nombre completo del candidato y su título profesional actual del CV)
          
          Párrafo 2 - Experiencia relevante:
          - Destaca la experiencia más relevante del CV para el puesto
          - Menciona tecnologías y habilidades específicas del CV que coincidan con los requisitos
          - Usa números y logros concretos de las experiencias del CV
          
          Párrafo 3 - Valor agregado:
          - Explica por qué el candidato es ideal para este puesto específico
          - Conecta las habilidades del CV con las responsabilidades del trabajo
          - Muestra entusiasmo genuino por la oportunidad
          
          Párrafo 4 - Cierre:
          - Agradecimiento por el tiempo
          - Disposición para una entrevista
          - Despedida profesional
          
          FORMATO JSON de respuesta:
          {
            "paragraphs": [
              "Párrafo 1 completo...",
              "Párrafo 2 completo...",
              "Párrafo 3 completo...",
              "Párrafo 4 completo..."
            ]
          }
          
          EJEMPLOS DE LO QUE NO DEBES HACER:
          ❌ "Me dirijo a usted para expresar mi interés en el puesto de [Título del Puesto]"
          ❌ "en [Nombre de la Empresa]"
          ❌ "Estimado/a [Nombre del Reclutador]"
          ❌ "Me impresiona la misión de [Nombre de la Empresa]"
          
          EJEMPLOS DE LO QUE SÍ DEBES HACER:
          ✅ "Me dirijo a ustedes para expresar mi interés en el puesto de Data Scientist & AI" (usando el título del CV)
          ✅ "Con mi sólida formación en ciencia de datos y experiencia en proyectos de análisis clínico" (usando info del CV)
          ✅ "Estimado/a Equipo de Recursos Humanos" (cuando no hay nombre específico)
          
          INFORMACIÓN EXTRA:
          - La fecha actual es ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
          - Usa esta fecha en el encabezado de la carta
          
          IMPORTANTE: Debes responder ÚNICAMENTE con un objeto JSON válido, SIN placeholders ni corchetes en el contenido.`
        },
        {
          role: "user",
          content: `Genera una carta de presentación profesional usando SOLO esta información REAL:

INFORMACIÓN DEL CANDIDATO (CV):
${JSON.stringify(cvData, null, 2)}

DESCRIPCIÓN DEL TRABAJO:
${jobDetails}

Instrucciones:
1. Lee toda la información del CV (nombre, experiencia, habilidades, educación)
2. Lee la descripción del trabajo para entender los requisitos
3. Crea una carta que conecte la experiencia real del candidato con los requisitos del puesto
4. NO uses ningún placeholder o texto entre corchetes []
5. Si no tienes información específica, omite esa parte o usa alternativas genéricas apropiadas

Por favor, genera la carta en formato JSON como se especificó.`
        }
      ],
      temperature: 0.3,
      response_format: { type: "json_object" }
    });

    return JSON.parse(completion.choices[0].message.content || '{}');
  } catch (error) {
    console.error('Error generando carta:', error);
    throw error;
  }
};

// Función auxiliar para extraer el año más reciente de un período
function extractLatestYear(period: string): number {
  if (!period) return 0;

  // Buscar todos los números de 4 dígitos (años) en el string
  const years = period.match(/\b\d{4}\b/g);
  if (!years || years.length === 0) return 0;

  // Devolver el año más alto (más reciente)
  return Math.max(...years.map(y => parseInt(y)));
}

// Función para el chatbot de asistencia en postulaciones
export const getChatbotResponse = async (userMessage: string, cvData: any, previousMessages: any[] = []) => {
  try {
    // Preparar los mensajes del sistema y usuario
    const systemMessage = {
      role: "system" as const,
      content: `Eres un asistente de postulación laboral amigable y útil. Tu objetivo es ayudar a los usuarios a prepararse para entrevistas laborales, responder preguntas sobre cómo mejorar su CV y dar consejos para el proceso de postulación.

      Información del usuario:
      - Nombre: ${cvData?.name || 'Usuario'}
      - Profesión: ${cvData?.title || 'No especificada'}
      - Habilidades: ${cvData?.skills?.join(', ') || 'No especificadas'}
      - Experiencia: ${cvData?.experience?.length || 0} experiencias registradas
      
      Instrucciones importantes:
      1. Sé conciso y directo en tus respuestas.
      2. Si te preguntan sobre salario, propone respuestas estratégicas.
      3. Si te preguntan sobre debilidades, sugiere respuestas que muestren crecimiento profesional.
      4. Si te preguntan sobre la experiencia del usuario, utiliza la información del CV para contextualizar.
      5. Siempre sé positivo y motivador, pero realista.
      6. Tus respuestas deben ser breves (máximo 3-4 oraciones).
      7. Responde siempre en español.
      
      Formato de respuesta: 
      - Da directamente la respuesta profesional que el usuario podría usar, sin prefijos como "Puedes responder" y sin usar comillas.
      - No dividas tu respuesta en partes. Proporciona solo el texto que el usuario puede copiar y pegar.
      - Comienza inmediatamente con la respuesta que el usuario puede usar en su entrevista o postulación.`
    };

    const messagesForAPI = [
      systemMessage,
      ...previousMessages,
      { role: "user" as const, content: userMessage }
    ];

    // Llamar a la API de Groq
    const completion = await groq.chat.completions.create({
      model: MODELS.CHATBOT,
      messages: messagesForAPI,
      temperature: 0.7,
      max_tokens: 500
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error('Error en el chatbot:', error);
    return "Lo siento, ha ocurrido un error al procesar tu pregunta. Por favor, intenta nuevamente.";
  }
};

/**
 * Genera un título creativo y profesional para el CV usando IA
 */
export const generateCVTitle = async (jobTitle: string, candidateName?: string): Promise<string> => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.CHATBOT,
      messages: [
        {
          role: "system",
          content: "Eres un experto en branding profesional. Genera títulos creativos para CVs. Formato: CV [Puesto] - [Fecha]. Máximo 50 caracteres. Responde SOLO con el título."
        },
        {
          role: "user",
          content: `Título para: ${jobTitle}`
        }
      ],
      temperature: 0.8,
      max_tokens: 60
    });

    return completion.choices[0].message.content?.trim() || `CV ${jobTitle} - ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}`;
  } catch (error) {
    return `CV ${jobTitle} - ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}`;
  }
};

/**
 * Extrae el nombre de la empresa de la descripción del trabajo usando IA
 * @param jobDescription - Descripción completa del trabajo
 * @returns Nombre de la empresa o null si no se detecta
 */
export const extractCompanyName = async (jobDescription: string): Promise<string | null> => {
  try {
    const completion = await groq.chat.completions.create({
      model: MODELS.JOB_ANALYSIS,
      messages: [
        {
          role: "system",
          content: "Eres un experto en análisis de ofertas de trabajo. Tu ÚNICA tarea es extraer el nombre de la empresa/compañía. Responde SOLO con el nombre de la empresa, nada más. Si NO encuentras el nombre de la empresa, responde exactamente: null"
        },
        {
          role: "user",
          content: `Extrae SOLO el nombre de la empresa de esta oferta:

${jobDescription}

Respuesta (solo nombre o null):`
        }
      ],
      temperature: 0.1,
      max_tokens: 50
    });

    const result = completion.choices[0].message.content?.trim();

    // Si la IA respondió "null" o está vacío, retornar null
    if (!result || result.toLowerCase() === "null" || result.length < 2) {
      return null;
    }

    return result;
  } catch (error) {
    console.error("Error extrayendo nombre de empresa:", error);
    return null;
  }
};
